import { flow, isNil } from 'lodash-es';
import { ReactNode } from 'react';
import { AssistantPerfRes, PerfAmount } from 'services/api/dashboard';
import {
  currencyDisplay,
  getAmountByCurrency,
  minuteDurationDisplay,
  numDisplay,
  numDisplayWithNone,
  percentDisplay,
  percentDisplayWithNone,
  secondDurationDisplay,
} from 'views/Dashboard/ImplementData/utils';
import { Group, SubGroup } from './config';

type Currency = Exclude<Dashboard.CurrencyShorthand, 'RMB'>;

type AmountRender = {
  type: 'amount';
  render: (amount: PerfAmount, currency?: Currency) => ReactNode;
};

export interface Column {
  key: string;
  tooltip?: string;
  show?: boolean;
  render?: ((value: any) => ReactNode) | AmountRender;
  dataIndex: keyof AssistantPerfRes;
  index: number;
  group?: Group;
  subGroup?: SubGroup;
  fixed?: 'left' | 'right';
  col?: number;
  width?: number;
  sortIndex?: number;
}

const amountRender: AmountRender = {
  type: 'amount',
  render: (amount, currency) => {
    const amountCount = getAmountByCurrency(currency, amount?.currencyToAmount);

    return flow(emptyDataMap, currencyDisplay)(amountCount);
  },
};
/**
 * 将后端返回结果的负数映射为 undefined
 * 分为两种情况：
 * 1. 当前筛选项没有数据时为负数 -1
 * 2. 当前筛选项有数据但是数据为0
 * @param value  数字
 * @returns 过滤数据结果
 */
export const emptyDataMap = (value?: number) => {
  if (value === undefined) {
    return undefined;
  }
  const num = Number(value);
  if (num === -1) {
    return undefined;
  }
  if (num === -2) {
    return 'None';
  }
  return value;
};
/**
 * 数据为空时显示 '-'
 * @param value 数字
 * @returns 渲染结果
 */
export const emptyDataDisplay = (value?: number) => {
  if (isNil(value)) {
    return '-';
  }
  return value;
};
export const columns: Column[] = [
  {
    key: 'ID',
    show: true,
    index: 0,
    width: 40,
    fixed: 'left',
    dataIndex: 'assistantId',
  },
  {
    key: '帐号昵称',
    show: true,
    index: 1,
    width: 80,
    fixed: 'left',
    dataIndex: 'assistantName',
  },
  {
    key: '帐号在线总时长',
    tooltip: '帐号在线总时长tips_copy',
    show: true,
    index: 2,
    dataIndex: 'onlineMin',
    render: minuteDurationDisplay,
  },
  {
    key: '帐号挂起总时长',
    tooltip: '帐号挂起总时长tips_copy',
    show: true,
    index: 2.5,
    dataIndex: 'hangMin',
    render: minuteDurationDisplay,
  },
  {
    key: '需要接待买家数',
    tooltip: '需要接待买家数tips',
    show: true,
    index: 3,
    width: 200,
    group: '参考指标',
    subGroup: '接待',
    dataIndex: 'conversationCount',
    render: numDisplay,
  },
  {
    key: '完成接待买家数',
    tooltip: '完成接待买家数tips',
    show: false,
    index: 4,
    group: '参考指标',
    subGroup: '接待',
    dataIndex: 'completeRspConversationCount',
    render: numDisplay,
  },
  {
    key: '未完成接待买家数',
    tooltip: '未完成接待买家数tips',
    show: false,
    index: 5,
    group: '参考指标',
    subGroup: '接待',
    dataIndex: 'badConversationCount',
    render: numDisplay,
  },
  {
    key: '人工客服完成接待买家数',
    tooltip: '人工客服完成接待买家数tips',
    show: true,
    index: 6,
    group: '客服投入',
    subGroup: '接待',
    dataIndex: 'goodConversationCount',
    render: numDisplay,
  },
  {
    key: '人工参与接待买家数',
    tooltip: '人工参与接待买家数tips_copy',
    show: false,
    index: 7,
    group: '客服投入',
    subGroup: '接待',
    dataIndex: 'asstPartReceptionCount',
    render: numDisplay,
  },
  {
    key: '人工参与接待买家数（仅售前买家）',
    tooltip: '人工参与接待买家数（仅售前买家）tips',
    show: false,
    index: 7.5,
    group: '客服投入',
    subGroup: '接待',
    dataIndex: 'asstPartReceptionCountPreSale',
    render: numDisplay,
  },
  {
    key: '需要人工客服接待买家数',
    tooltip: '需要人工客服接待买家数tips',
    show: false,
    index: 8,
    width: 160,
    group: '参考指标',
    subGroup: '接待',
    dataIndex: 'needAssRspConversationCount',
    render: numDisplay,
  },
  {
    key: '机器人客服或主管完成接待买家数',
    tooltip: '机器人客服或主管完成接待买家数tips',
    show: false,
    index: 9,
    width: 190,
    group: '参考指标',
    subGroup: '接待',
    dataIndex: 'mediumConversationCount',
    render: numDisplay,
  },
  {
    key: '人工最后一次下线时待处理买家数',
    tooltip: '人工最后一次下线时待处理买家数tips',
    show: false,
    index: 10,
    group: '参考指标',
    subGroup: '接待',
    dataIndex: 'untreatedCount',
    render: numDisplay,
  },
  {
    key: '已解决重要提醒买家数',
    tooltip: '已解决重要提醒买家数tips_copy',
    show: true,
    index: 11,
    width: 150,
    group: '客服投入',
    subGroup: '接待',
    dataIndex: 'rspRemindRecoderCount',
    render: numDisplay,
  },
  {
    key: '人工操作取消订单数',
    tooltip: '人工操作取消订单数tips_copy',
    show: false,
    index: 12,
    group: '客服投入',
    subGroup: '取消订单',
    dataIndex: 'handleCancelOrderCount',
    render: numDisplay,
  },
  {
    key: '人工对取消订单发起的沟通数',
    tooltip: '人工对取消订单发起的沟通数tips_copy',
    show: true,
    index: 13,
    width: 160,
    group: '客服投入',
    subGroup: '取消订单',
    dataIndex: 'rspCancelOrderCount',
    render: numDisplay,
  },
  {
    key: '人工操作仅退款订单数',
    tooltip: '人工操作仅退款订单数tips_copy',
    show: false,
    index: 14,
    group: '客服投入',
    subGroup: '仅退款',
    dataIndex: 'handleOnlyRefundOrderCount',
    render: numDisplay,
  },
  {
    key: '人工对仅退款订单发起的沟通数',
    tooltip: '人工对仅退款订单发起的沟通数tips_copy',
    show: true,
    index: 15,
    width: 160,
    group: '客服投入',
    subGroup: '仅退款',
    dataIndex: 'rspOnlyRefundOrderCount',
    render: numDisplay,
  },
  {
    key: '人工操作退货退款订单申请数',
    tooltip: '人工操作退货退款订单申请数tips_copy',
    show: false,
    index: 16,
    group: '客服投入',
    subGroup: '退货退款',
    dataIndex: 'handleReturnOrderCount',
    render: numDisplay,
  },
  {
    key: '人工对退货退款订单发起的沟通数',
    tooltip: '人工对退货退款订单发起的沟通数tips_copy',
    show: true,
    index: 17,
    width: 165,
    group: '客服投入',
    subGroup: '退货退款',
    dataIndex: 'rspReturnOrderCount',
    render: numDisplay,
  },
  {
    key: '人工回评数（中差评）',
    tooltip: '人工回评数（中差评）tips_copy',
    show: false,
    index: 18,
    group: '客服投入',
    subGroup: '评价',
    dataIndex: 'handleBadCommentCount',
    render: numDisplay,
  },
  {
    key: '人工回评总数',
    tooltip: '人工回评总数tips_copy',
    show: false,
    index: 19,
    group: '客服投入',
    subGroup: '评价',
    dataIndex: 'handleCommentCount',
    render: numDisplay,
  },
  {
    key: '人工客服对中差评发起的沟通数',
    tooltip: '人工客服对中差评发起的沟通数tips_copy',
    show: true,
    index: 20,
    width: 160,
    group: '客服投入',
    subGroup: '评价',
    dataIndex: 'rspBadCommentCount',
    render: numDisplay,
  },
  {
    key: '接待后下单的订单数',
    tooltip: '接待后下单的订单数tips_copy',
    show: true,
    index: 21,
    width: 150,
    group: '工作成果',
    subGroup: '下单',
    dataIndex: 'rspOrderCount',
    render: numDisplay,
  },
  {
    key: '接待后下单的买家数',
    tooltip: '接待后下单的买家数tips_copy',
    show: false,
    index: 22,
    width: 150,
    group: '工作成果',
    subGroup: '下单',
    dataIndex: 'rspOrderBuyerCount',
    render: numDisplay,
  },
  {
    key: '接待后下单的订单金额',
    tooltip: '接待后下单的订单金额tips_copy',
    show: false,
    index: 23,
    width: 180,
    group: '工作成果',
    subGroup: '下单',
    dataIndex: 'rspOrderTradeAmount',
    render: amountRender,
  },
  {
    key: '接待后下单的订单数(仅售前买家)',
    tooltip: '接待后下单的订单数(仅售前买家)tips_copy',
    show: false,
    index: 24,
    width: 180,
    group: '工作成果',
    subGroup: '下单',
    dataIndex: 'preSaleRspOrderCount',
    render: numDisplay,
  },
  {
    key: '接待后下单的买家数(仅售前买家)',
    tooltip: '接待后下单的买家数(仅售前买家)tips_copy',
    show: false,
    index: 25,
    width: 180,
    group: '工作成果',
    subGroup: '下单',
    dataIndex: 'preSaleRspOrderBuyerCount',
    render: numDisplay,
  },
  {
    key: '接待后下单的订单金额(仅售前买家)',
    tooltip: '接待后下单的订单金额(仅售前买家)tips_copy',
    show: false,
    index: 26,
    width: 200,
    group: '工作成果',
    subGroup: '下单',
    dataIndex: 'preSaleRspOrderTradeAmount',
    render: amountRender,
  },
  {
    key: '接待后付款的订单数',
    tooltip: '接待后付款的订单数tips_copy',
    show: true,
    index: 27,
    width: 150,
    group: '工作成果',
    subGroup: '付款',
    dataIndex: 'rspPayOrderCount',
    render: numDisplay,
  },
  {
    key: '接待后付款的买家数',
    tooltip: '接待后付款的买家数tips_copy',
    show: false,
    index: 28,
    width: 150,
    group: '工作成果',
    subGroup: '付款',
    dataIndex: 'rspPayOrderBuyerCount',
    render: numDisplay,
  },
  {
    key: '接待后付款的订单金额',
    tooltip: '接待后付款的订单金额tips_copy',
    show: false,
    index: 29,
    width: 180,
    group: '工作成果',
    subGroup: '付款',
    dataIndex: 'rspPayOrderTradeAmount',
    render: amountRender,
  },
  {
    key: '接待后付款的订单数(仅售前买家)',
    tooltip: '接待后付款的订单数(仅售前买家)tips_copy',
    show: false,
    index: 30,
    width: 180,
    group: '工作成果',
    subGroup: '付款',
    dataIndex: 'preSaleRspPayOrderCount',
    render: numDisplay,
  },
  {
    key: '接待后付款的买家数(仅售前买家)',
    tooltip: '接待后付款的买家数(仅售前买家)tips_copy',
    show: false,
    index: 31,
    width: 180,
    group: '工作成果',
    subGroup: '付款',
    dataIndex: 'preSaleRspPayOrderBuyerCount',
    render: numDisplay,
  },
  {
    key: '接待后付款的订单金额(仅售前买家)',
    tooltip: '接待后付款的订单金额(仅售前买家)tips_copy',
    show: false,
    index: 32,
    width: 200,
    group: '工作成果',
    subGroup: '付款',
    dataIndex: 'preSaleRspPayOrderTradeAmount',
    render: amountRender,
  },
  {
    key: '沟通后挽回的取消订单数',
    tooltip: '沟通后挽回的取消订单数tips_copy',
    show: true,
    index: 33,
    width: 150,
    group: '工作成果',
    subGroup: '取消订单',
    dataIndex: 'rspCancelOrderIsRecoveredCount',
    render: numDisplay,
    col: 3,
  },
  {
    key: '沟通后撤销的取消订单数',
    tooltip: '沟通后撤销的取消订单数tips_copy',
    show: false,
    index: 34,
    width: 180,
    group: '工作成果',
    subGroup: '取消订单',
    dataIndex: 'rspCancelOrderIsCancelledCount',
    render: numDisplay,
  },
  {
    key: '沟通后撤销的取消订单金额',
    tooltip: '沟通后撤销的取消订单金额tips_copy',
    show: false,
    index: 35,
    width: 190,
    group: '工作成果',
    subGroup: '取消订单',
    dataIndex: 'rspCancelOrderIsCancelledTradeAmount',
    render: amountRender,
    col: 2,
  },
  {
    key: '沟通后再下单的取消订单数',
    tooltip: '沟通后再下单的取消订单数tips_copy',
    show: false,
    index: 36,
    width: 180,
    group: '工作成果',
    subGroup: '取消订单',
    dataIndex: 'rspCancelOrderIsReorderCount',
    render: numDisplay,
  },
  {
    key: '沟通后取消订单再下单的订单数',
    tooltip: '沟通后取消订单再下单的订单数tips_copy',
    show: false,
    index: 37,
    group: '工作成果',
    width: 180,
    subGroup: '取消订单',
    dataIndex: 'rspCancelOrderIsReorderToTradeCount',
    render: numDisplay,
  },
  {
    key: '沟通后取消订单再下单的订单金额',
    tooltip: '沟通后取消订单再下单的订单金额tips_copy',
    show: false,
    index: 38,
    width: 200,
    group: '工作成果',
    subGroup: '取消订单',
    dataIndex: 'rspCancelOrderIsReorderToTradeAmount',
    render: amountRender,
  },
  {
    key: '沟通后挽回的仅退款订单数',
    tooltip: '沟通后挽回的仅退款订单数tips_copy',
    show: true,
    index: 39,
    width: 150,
    group: '工作成果',
    subGroup: '仅退款',
    dataIndex: 'rspOnlyRefundOrderIsRecoveredCount',
    render: numDisplay,
    col: 3,
  },
  {
    key: '沟通后撤销的仅退款订单数',
    tooltip: '沟通后撤销的仅退款订单数tips_copy',
    show: false,
    index: 40,
    width: 200,
    group: '工作成果',
    subGroup: '仅退款',
    dataIndex: 'rspOnlyRefundOrderIsCancelledCount',
    render: numDisplay,
  },
  {
    key: '沟通后撤销的仅退款订单金额',
    tooltip: '沟通后撤销的仅退款订单金额tips_copy',
    show: false,
    index: 41,
    width: 200,
    group: '工作成果',
    subGroup: '仅退款',
    dataIndex: 'rspOnlyRefundOrderIsCancelledTradeAmount',
    render: amountRender,
    col: 2,
  },
  {
    key: '沟通后再下单的仅退款订单数',
    tooltip: '沟通后再下单的仅退款订单数tips_copy',
    show: false,
    index: 42,
    width: 180,
    group: '工作成果',
    subGroup: '仅退款',
    dataIndex: 'rspOnlyRefundOrderIsReorderCount',
    render: numDisplay,
  },
  {
    key: '沟通后仅退款订单再下单的订单数',
    tooltip: '沟通后仅退款订单再下单的订单数tips_copy',
    show: false,
    index: 43,
    width: 180,
    group: '工作成果',
    subGroup: '仅退款',
    dataIndex: 'rspOnlyRefundOrderIsReorderToTradeCount',
    render: numDisplay,
  },
  {
    key: '沟通后仅退款订单再下单的订单金额',
    tooltip: '沟通后仅退款订单再下单的订单金额tips_copy',
    show: false,
    index: 44,
    width: 200,
    group: '工作成果',
    subGroup: '仅退款',
    dataIndex: 'rspOnlyRefundOrderIsReorderToTradeAmount',
    render: amountRender,
  },
  {
    key: '沟通后挽回的退货退款订单数',
    tooltip: '沟通后挽回的退货退款订单数tips_copy',
    show: true,
    index: 45,
    width: 180,
    group: '工作成果',
    subGroup: '退货退款',
    dataIndex: 'rspReturnOrderIsRecoveredCount',
    render: numDisplay,
    col: 3,
  },
  {
    key: '沟通后撤销的退货退款订单数',
    tooltip: '沟通后撤销的退货退款订单数tips_copy',
    show: false,
    index: 46,
    width: 180,
    group: '工作成果',
    subGroup: '退货退款',
    dataIndex: 'rspReturnOrderIsCancelledCount',
    render: numDisplay,
  },
  {
    key: '沟通后撤销的退货退款订单金额',
    tooltip: '沟通后撤销的退货退款订单金额tips_copy',
    show: false,
    index: 47,
    width: 210,
    group: '工作成果',
    subGroup: '退货退款',
    dataIndex: 'rspReturnOrderIsCancelledTradeAmount',
    render: amountRender,
    col: 2,
  },
  {
    key: '沟通后再下单的退货退款订单数',
    tooltip: '沟通后再下单的退货退款订单数tips_copy',
    show: false,
    index: 48,
    width: 180,
    group: '工作成果',
    subGroup: '退货退款',
    dataIndex: 'rspReturnOrderIsReorderCount',
    render: numDisplay,
  },
  {
    key: '沟通后退货退款订单再下单的订单数',
    tooltip: '沟通后退货退款订单再下单的订单数tips_copy',
    show: false,
    index: 49,
    width: 180,
    group: '工作成果',
    subGroup: '退货退款',
    dataIndex: 'rspReturnOrderIsReorderToTradeCount',
    render: numDisplay,
  },
  {
    key: '沟通后退货退款订单再下单的订单金额',
    tooltip: '沟通后退货退款订单再下单的订单金额tips_copy',
    show: false,
    index: 50,
    width: 200,
    group: '工作成果',
    subGroup: '退货退款',
    dataIndex: 'rspReturnOrderIsReorderToTradeAmount',
    render: amountRender,
  },
  {
    key: '沟通后72小时内中差评修改为好评的评价数',
    tooltip: '沟通后72小时内中差评修改为好评的评价数tips_copy',
    show: true,
    index: 51,
    width: 200,
    group: '工作成果',
    subGroup: '评价',
    dataIndex: 'rspBadCommentIsRecoveredCount',
    render: numDisplay,
  },
  {
    key: '人工参与接待后的中差评评价数',
    tooltip: '人工参与接待后的中差评评价数tips',
    show: false,
    index: 52,
    width: 180,
    group: '工作成果',
    subGroup: '评价',
    dataIndex: 'asstGenBadCommentCount',
    render: numDisplay,
  },
  {
    key: '人工参与接待后有评价内容的中差评评价数',
    tooltip: '人工参与接待后有评价内容的中差评评价数tips',
    show: false,
    index: 53,
    width: 200,
    group: '工作成果',
    subGroup: '评价',
    dataIndex: 'asstGenBadCommentWithContentCount',
    render: numDisplay,
  },
  {
    key: '沟通后30天内中差评修改为好评的评价数',
    tooltip: '沟通后30天内中差评修改为好评的评价数tips_copy',
    show: true,
    index: 51.1,
    width: 200,
    group: '工作成果',
    subGroup: '评价',
    dataIndex: 'rspBadCommentIsRecoveredCountThirtyDays',
    render: numDisplay,
  },
  {
    key: '人工1分钟内响应率',
    tooltip: '人工1分钟内响应率tips',
    show: true,
    index: 54,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstRspTimeIn1MinRatio',
    render: percentDisplay,
  },
  {
    key: '人工5分钟内响应率',
    tooltip: '人工5分钟内响应率tips',
    show: true,
    index: 55,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstRspTimeIn5MinRatio',
    render: percentDisplay,
  },
  {
    key: '人工10分钟内响应率',
    tooltip: '人工10分钟内响应率tips',
    show: true,
    index: 55.1,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstRspTimeIn10MinRatio',
    render: percentDisplay,
  },
  {
    key: '人工15分钟内响应率',
    tooltip: '人工15分钟内响应率tips',
    show: true,
    index: 55.2,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstRspTimeIn15MinRatio',
    render: percentDisplay,
  },
  {
    key: '人工30分钟内响应率',
    tooltip: '人工30分钟内响应率tips',
    show: true,
    index: 56,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstRspTimeIn30MinRatio',
    render: percentDisplay,
  },
  {
    key: '人工60分钟内响应率',
    tooltip: '人工60分钟内响应率tips',
    show: true,
    index: 57,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstRspTimeIn60MinRatio',
    render: percentDisplay,
  },
  {
    key: '买家咨询1分钟内被接待率',
    tooltip: '买家咨询1分钟内被接待率tips',
    show: true,
    index: 58,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstBuyerWaitTime1MinRatio',
    render: percentDisplay,
  },
  {
    key: '买家咨询5分钟内被接待率',
    tooltip: '买家咨询5分钟内被接待率tips',
    show: true,
    index: 59,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstBuyerWaitTime5MinRatio',
    render: percentDisplay,
  },
  {
    key: '买家咨询10分钟内被接待率',
    tooltip: '买家咨询10分钟内被接待率tips',
    show: true,
    index: 59.1,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstBuyerWaitTime10MinRatio',
    render: percentDisplay,
  },
  {
    key: '买家咨询15分钟内被接待率',
    tooltip: '买家咨询15分钟内被接待率tips',
    show: true,
    index: 59.2,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstBuyerWaitTime15MinRatio',
    render: percentDisplay,
  },
  {
    key: '买家咨询30分钟内被接待率',
    tooltip: '买家咨询30分钟内被接待率tips',
    show: true,
    index: 60,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstBuyerWaitTime30MinRatio',
    render: percentDisplay,
  },
  {
    key: '买家咨询60分钟内被接待率',
    tooltip: '买家咨询60分钟内被接待率tips',
    show: true,
    index: 61,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'asstBuyerWaitTime60MinRatio',
    render: percentDisplay,
  },
  {
    key: '人工客服接待平均响应时长',
    tooltip: '人工客服接待平均响应时长tips_copy',
    show: true,
    index: 62,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'avgRspLoginEventTimeMin',
    render: minuteDurationDisplay,
  },
  {
    key: '买家咨询平均等待接待时长',
    tooltip: '买家咨询平均等待接待时长tips_copy',
    show: false,
    index: 63,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'avgRspTimeMin',
    render: minuteDurationDisplay,
  },
  {
    key: '重要提醒解决平均耗时',
    tooltip: '重要提醒解决平均耗时tips_copy',
    show: false,
    index: 64,
    width: 150,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'remindRecordElapsedTime',
    render: secondDurationDisplay,
  },
  {
    key: '人工客服接待完成率',
    tooltip: '人工客服接待完成率tips',
    show: true,
    index: 65,
    width: 180,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'participateResponseRate',
    render: percentDisplay,
  },
  {
    key: '接待完成率',
    tooltip: '接待完成率tips',
    show: true,
    index: 66,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'overallResponseRate',
    render: percentDisplay,
  },
  {
    key: '接待后下单转化率',
    tooltip: '接待后下单转化率tips',
    show: false,
    index: 67,
    width: 180,
    group: '服务水平',
    subGroup: '下单',
    dataIndex: 'overallOrderConvertRatio',
    render: percentDisplay,
  },
  {
    key: '接待后下单转化率(仅售前买家)',
    tooltip: '接待后下单转化率(仅售前买家)tips',
    show: true,
    index: 68,
    width: 150,
    group: '服务水平',
    subGroup: '下单',
    dataIndex: 'preSaleOrderConvertRatio',
    render: percentDisplay,
  },
  {
    key: '接待后付款转化率',
    tooltip: '接待后付款转化率tips',
    show: false,
    index: 69,
    width: 180,
    group: '服务水平',
    subGroup: '付款',
    dataIndex: 'overallPayOrderConvertRatio',
    render: percentDisplay,
  },
  {
    key: '接待后付款转化率(仅售前买家)',
    tooltip: '接待后付款转化率(仅售前买家)tips',
    show: true,
    index: 70,
    width: 150,
    group: '服务水平',
    subGroup: '付款',
    dataIndex: 'preSalePayOrderConvertRatio',
    render: percentDisplay,
  },
  {
    key: '取消订单挽回率',
    tooltip: '取消订单挽回率tips',
    show: true,
    index: 71,
    group: '服务水平',
    subGroup: '取消订单',
    dataIndex: 'rspCancelOrderConvertRatio',
    render: percentDisplay,
  },
  {
    key: '取消订单发起沟通平均时长',
    tooltip: '取消订单发起沟通平均时长tips',
    show: false,
    index: 72,
    group: '服务水平',
    subGroup: '取消订单',
    dataIndex: 'avgCancelOrderRspTimeGap',
    render: secondDurationDisplay,
  },
  {
    key: '仅退款挽回率',
    tooltip: '仅退款挽回率tips',
    show: true,
    index: 73,
    group: '服务水平',
    subGroup: '仅退款',
    dataIndex: 'rspOnlyRefundOrderConvertRatio',
    render: percentDisplay,
  },
  {
    key: '仅退款订单发起沟通平均时长',
    tooltip: '仅退款订单发起沟通平均时长tips',
    show: false,
    index: 74,
    group: '服务水平',
    subGroup: '仅退款',
    dataIndex: 'avgOnlyRefundOrderRspTimeGap',
    render: secondDurationDisplay,
  },
  {
    key: '退货退款挽回率',
    tooltip: '退货退款挽回率tips',
    show: true,
    index: 75,
    group: '服务水平',
    subGroup: '退货退款',
    dataIndex: 'rspReturnOrderConvertRatio',
    render: percentDisplay,
  },
  {
    key: '退货退款订单发起沟通平均时长',
    tooltip: '退货退款订单发起沟通平均时长tips',
    show: false,
    index: 76,
    group: '服务水平',
    subGroup: '退货退款',
    dataIndex: 'avgReturnOrderRspTimeGap',
    render: secondDurationDisplay,
  },
  {
    key: '中差评挽回率(72小时)',
    tooltip: '中差评挽回率(72小时)tips',
    show: true,
    index: 77,
    group: '服务水平',
    subGroup: '评价',
    dataIndex: 'rspBadCommentConvertRatio',
    render: percentDisplay,
  },
  {
    key: '中差评发起沟通平均时长',
    tooltip: '中差评发起沟通平均时长tips',
    show: false,
    index: 78,
    col: 2,
    group: '服务水平',
    subGroup: '评价',
    dataIndex: 'avgBadCommentRspTimeGap',
    render: secondDurationDisplay,
  },
  {
    key: '中差评挽回率(30天)',
    tooltip: '中差评挽回率(30天)tips',
    show: true,
    index: 77.1,
    group: '服务水平',
    subGroup: '评价',
    dataIndex: 'rspBadCommentConvertRatioThirtyDays',
    render: percentDisplay,
  },
  {
    key: '人工有接待权限店铺的中差评评价数',
    tooltip: '人工有接待权限店铺的中差评评价数tips',
    show: false,
    index: 79,
    width: 180,
    group: '参考指标',
    subGroup: '评价',
    dataIndex: 'asstStoreBadCommentCount',
    render: numDisplay,
  },
  {
    key: '人工参与接待后的中差评率',
    tooltip: '人工参与接待后的中差评率tips',
    show: false,
    index: 80,
    width: 180,
    group: '服务水平',
    subGroup: '评价',
    dataIndex: 'asstGenBadCommentRatio',
    render: percentDisplay,
  },
  {
    key: '人工参与接待后有评价内容的中差评率',
    tooltip: '人工参与接待后有评价内容的中差评率tips',
    show: false,
    index: 81,
    width: 200,
    group: '服务水平',
    subGroup: '评价',
    dataIndex: 'asstGenBadCommentWithContentRatio',
    render: percentDisplay,
  },
  {
    key: '提交知识数',
    show: false,
    index: 82,
    group: '客服投入',
    subGroup: '知识自学习',
    dataIndex: 'submittedCount',
    render: numDisplayWithNone,
  },
  {
    key: '贡献的AI回复数',
    show: false,
    index: 83,
    group: '工作成果',
    subGroup: '知识自学习',
    dataIndex: 'aiLearningReplyCount',
    render: numDisplayWithNone,
  },
  {
    key: '启用的知识数',
    show: false,
    index: 84,
    group: '工作成果',
    subGroup: '知识自学习',
    dataIndex: 'enabledCount',
    render: numDisplayWithNone,
  },
  {
    key: '提交率',
    tooltip: '提交率tips',
    show: false,
    index: 85,
    group: '服务水平',
    subGroup: '知识自学习',
    dataIndex: 'submittedRate',
    render: percentDisplayWithNone,
  },
  {
    key: '启用率',
    tooltip: '被采纳率tips',
    show: false,
    index: 85,
    group: '服务水平',
    subGroup: '知识自学习',
    dataIndex: 'enabledRate',
    render: percentDisplayWithNone,
  },
];

export const skintificColumns: Column[] = [
  {
    key: '人工接待后平均关闭会话时长',
    tooltip: '',
    show: true,
    index: 66.1,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'avgHandlingTime',
    render: secondDurationDisplay,
  },
  {
    key: '人工平均关闭会话时长',
    tooltip: '',
    show: true,
    index: 66.2,
    group: '服务水平',
    subGroup: '接待',
    dataIndex: 'avgFullResolutionTime',
    render: secondDurationDisplay,
  },
];

export const persistColumn: Column['key'][] = [
  '帐号昵称',
  'ID',
  '帐号在线总时长',
  '帐号挂起总时长',
];
export const cannotSortSelfColumn: Column['key'][] = ['帐号昵称', 'ID'];
export const amountColumn: Column['key'][] = [
  '接待后下单的订单金额',
  '接待后下单的订单金额(仅售前买家)',
  '接待后付款的订单金额',
  '接待后付款的订单金额(仅售前买家)',
  '沟通后撤销的取消订单金额',
  '沟通后取消订单再下单的订单金额',
  '沟通后撤销的仅退款订单金额',
  '沟通后仅退款订单再下单的订单金额',
  '沟通后撤销的退货退款订单金额',
  '沟通后退货退款订单再下单的订单金额',
];

export const dataIndexExportKeyMap = {
  onlineMin: '帐号在线总时长',
  hangMin: '帐号挂起总时长',
  conversationCount: '分配给客服帐号的买家数',
  completeRspConversationCount: '分配给客服帐号后被完成接待的买家数',
  badConversationCount: '分配给客服帐号后未被完成接待的买家数',
  goodConversationCount: '人工参与的完成接待买家数',
  asstPartReceptionCount: '人工参与接待买家数',
  asstPartReceptionCountPreSale: '人工参与接待买家数(仅售前买家)',
  needAssRspConversationCount: '需要人工参与接待买家数',
  mediumConversationCount: '人工未参与的完成接待买家数',
  untreatedCount: '人工最后一次下线时待处理买家数',
  rspRemindRecoderCount: '已解决重要提醒买家数',
  handleCancelOrderCount: '人工操作取消订单数',
  rspCancelOrderCount: '人工对取消订单发起的沟通数',
  handleOnlyRefundOrderCount: '人工操作仅退款订单数',
  rspOnlyRefundOrderCount: '人工对仅退款订单发起的沟通数',
  handleReturnOrderCount: '人工操作退货退款订单申请数',
  rspReturnOrderCount: '人工对退货退款订单发起的沟通数',
  handleBadCommentCount: '人工回评数(中差评)',
  handleCommentCount: '人工回评总数',
  rspBadCommentCount: '人工对中差评发起的沟通数',
  rspOrderCount: '人工参与接待后下单的订单数',
  rspOrderBuyerCount: '人工参与接待后下单的买家数',
  rspOrderTradeAmount: '人工参与接待后下单的订单金额',
  preSaleRspOrderCount: '人工参与接待后下单的订单数(仅售前买家)',
  preSaleRspOrderBuyerCount: '人工参与接待后下单的买家数(仅售前买家)',
  preSaleRspOrderTradeAmount: '人工参与接待后下单的订单金额(仅售前买家)',
  rspPayOrderCount: '人工参与接待后付款的订单数',
  rspPayOrderBuyerCount: '人工参与接待后付款的买家数',
  rspPayOrderTradeAmount: '人工参与接待后付款的订单金额',
  preSaleRspPayOrderCount: '人工参与接待后付款的订单数(仅售前买家)',
  preSaleRspPayOrderBuyerCount: '人工参与接待后付款的买家数(仅售前买家)',
  preSaleRspPayOrderTradeAmount: '人工参与接待后付款的订单金额(仅售前买家)',
  rspCancelOrderIsRecoveredCount: '沟通后挽回的取消订单数',
  rspCancelOrderIsCancelledCount: '沟通后撤销的取消订单数',
  rspCancelOrderIsCancelledTradeAmount: '沟通后撤销的取消订单金额',
  rspCancelOrderIsReorderCount: '沟通后再下单的取消订单数',
  rspCancelOrderIsReorderToTradeCount: '沟通后取消订单再下单的订单数',
  rspCancelOrderIsReorderToTradeAmount: '沟通后取消订单再下单的订单金额',
  rspOnlyRefundOrderIsRecoveredCount: '沟通后挽回的仅退款订单数',
  rspOnlyRefundOrderIsCancelledCount: '沟通后撤销的仅退款订单数',
  rspOnlyRefundOrderIsCancelledTradeAmount: '沟通后撤销的仅退款订单金额',
  rspOnlyRefundOrderIsReorderCount: '沟通后再下单的仅退款订单数',
  rspOnlyRefundOrderIsReorderToTradeCount: '沟通后仅退款订单再下单的订单数',
  rspOnlyRefundOrderIsReorderToTradeAmount: '沟通后仅退款订单再下单的订单金额',
  rspReturnOrderIsRecoveredCount: '沟通后挽回的退货退款订单数',
  rspReturnOrderIsCancelledCount: '沟通后撤销的退货退款订单数',
  rspReturnOrderIsCancelledTradeAmount: '沟通后撤销的退货退款订单金额',
  rspReturnOrderIsReorderCount: '沟通后再下单的退货退款订单数',
  rspReturnOrderIsReorderToTradeCount: '沟通后退货退款订单再下单的订单数',
  rspReturnOrderIsReorderToTradeAmount: '沟通后退货退款订单再下单的订单金额',
  rspBadCommentIsRecoveredCount: '沟通后72小时内中差评修改为好评的评价数',
  rspBadCommentIsRecoveredCountThirtyDays: '沟通后30天内中差评修改为好评的评价数',
  asstGenBadCommentCount: '人工参与接待后的中差评评价数',
  asstGenBadCommentWithContentCount: '人工参与接待后有评价内容的中差评评价数',
  asstRspTimeIn1MinRatio: '人工1分钟内响应率',
  asstRspTimeIn5MinRatio: '人工5分钟内响应率',
  asstRspTimeIn10MinRatio: '人工10分钟内响应率',
  asstRspTimeIn15MinRatio: '人工15分钟内响应率',
  asstRspTimeIn30MinRatio: '人工30分钟内响应率',
  asstRspTimeIn60MinRatio: '人工60分钟内响应率',
  asstBuyerWaitTime1MinRatio: '买家咨询1分钟内被接待率',
  asstBuyerWaitTime5MinRatio: '买家咨询5分钟内被接待率',
  asstBuyerWaitTime10MinRatio: '买家咨询10分钟内被接待率',
  asstBuyerWaitTime15MinRatio: '买家咨询15分钟内被接待率',
  asstBuyerWaitTime30MinRatio: '买家咨询30分钟内被接待率',
  asstBuyerWaitTime60MinRatio: '买家咨询60分钟内被接待率',
  avgRspLoginEventTimeMin: '人工接待平均响应时长',
  avgRspTimeMin: '买家咨询平均等待接待时长',
  remindRecordElapsedTime: '重要提醒解决平均耗时',
  participateResponseRate: '需要人工参与的买家接待完成率',
  overallResponseRate: '分配给客服帐号的买家接待完成率',
  overallOrderConvertRatio: '人工参与接待后下单转化率',
  preSaleOrderConvertRatio: '人工参与接待后下单转化率(仅售前买家)',
  overallPayOrderConvertRatio: '人工参与接待后付款转化率',
  preSalePayOrderConvertRatio: '人工参与接待后付款转化率(仅售前买家)',
  rspCancelOrderConvertRatio: '取消订单挽回率',
  avgCancelOrderRspTimeGap: '取消订单发起沟通平均时长',
  rspOnlyRefundOrderConvertRatio: '仅退款挽回率',
  avgOnlyRefundOrderRspTimeGap: '仅退款订单发起沟通平均时长',
  rspReturnOrderConvertRatio: '退货退款挽回率',
  avgReturnOrderRspTimeGap: '退货退款订单发起沟通平均时长',
  rspBadCommentConvertRatio: '中差评挽回率(72小时)',
  rspBadCommentConvertRatioThirtyDays: '中差评挽回率(30天)',
  avgBadCommentRspTimeGap: '中差评发起沟通平均时长',
  asstStoreBadCommentCount: '人工有接待权限店铺的中差评评价数',
  asstGenBadCommentRatio: '人工参与接待后的中差评率',
  asstGenBadCommentWithContentRatio: '人工参与接待后有评价内容的中差评率',
  avgHandlingTime: '人工接待后平均关闭会话时长',
  avgFullResolutionTime: '人工平均关闭会话时长',
  submittedCount: '提交知识数',
  aiLearningReplyCount: '贡献的AI回复数',
  enabledCount: '启用的知识数',
  submittedRate: '提交率',
  enabledRate: '启用率',
};
